import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  Award,
  Briefcase,
  Building2,
  CheckCircle,
  CreditCard,
  Edit2,
  FileText,
  History,
  KeyRound,
  Loader,
  Loader2,
  Lock,
  Mail,
  Phone,
  Save,
  Shield,
  User,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import {
  changePassword,
  getProfile,
  requestEmailChange,
  updateDepartment,
  updateMedicalLicenseNumber,
  updateName,
  verifyEmailChange,
  type ProfileData,
} from '../services/profileService';
import { getDoctorSubscriptionPlan, getMyPublicSubscription } from '../services/subscriptionService';
import type { SubscriptionPlan, PublicUserSubscription } from '../types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getErrorMessage = (error: unknown, fallback: string): string => {
  const requestError = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return requestError?.response?.data?.message || requestError?.message || fallback;
};

const getRoleLabel = (role: string): string => {
  if (role === ROLES.DOCTOR) return 'Doctor';
  if (role === ROLES.SUPERADMIN) return 'Super Admin';
  return role.replace('_', ' ').replace(/\b\w/g, (character) => character.toUpperCase());
};

const formatPlanPrice = (plan: SubscriptionPlan): string => {
  const amount = Number(plan.price);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: plan.currency,
    }).format(amount);
  } catch {
    return `${amount} ${plan.currency}`;
  }
};

const formatSubscriptionAmount = (
  amount: number | string | null | undefined,
  currency: string = 'USD'
): string => {
  const num = Number(amount) || 0;
  const curr = (currency || 'USD').toUpperCase().trim();
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${curr} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};

const formatSubscriptionDate = (dateStr?: string | null): string => {
  if (!dateStr) return 'Not specified';
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) return 'Not specified';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const subscriptionStatusClasses = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'bg-success-50 text-success-600';
    case 'pending':
      return 'bg-warning-50 text-warning-600';
    case 'expired':
      return 'bg-danger-100 text-danger-700';
    case 'cancelled':
      return 'bg-neutral-200 text-neutral-700';
    default:
      return 'bg-neutral-100 text-neutral-700';
  }
};

const formatAuditDate = (dateStr: string): string => {
  try {
    const parsed = new Date(dateStr);
    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const validateNewPassword = (password: string): string | null => {
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter';
  if (!/[a-z]/.test(password)) return 'Password must contain a lowercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain a number';
  if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain a special character';
  return null;
};

const Field: React.FC<{ label: string; htmlFor?: string; error?: string; children: React.ReactNode }> = ({
  label,
  htmlFor,
  error,
  children,
}) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block text-sm font-medium text-neutral-700">
      {label}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
  </div>
);

const Detail: React.FC<{
  label: string;
  icon: React.ElementType;
  value: string | number | null | undefined;
}> = ({ label, icon: Icon, value }) => (
  <div>
    <dt className="mb-1 flex items-center gap-1.5 text-sm font-medium text-neutral-700">
      <Icon className="h-3.5 w-3.5 text-neutral-400" />
      {label}
    </dt>
    <dd className="text-sm text-neutral-900">
      {value === null || value === undefined || value === '' ? 'Not specified' : String(value)}
    </dd>
  </div>
);

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [editedMedicalLicenseNumber, setEditedMedicalLicenseNumber] = useState('');
  const [editedDepartment, setEditedDepartment] = useState('');
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlan | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [publicSubscription, setPublicSubscription] = useState<PublicUserSubscription | null>(null);
  const [isLoadingPublicSubscription, setIsLoadingPublicSubscription] = useState(false);
  const [publicSubscriptionError, setPublicSubscriptionError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [passwordSubmitError, setPasswordSubmitError] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Countdown timer for OTP resend cooldown
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchProfile = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const data = await getProfile();
        if (isCurrentRequest) setProfileData(data);
      } catch (error) {
        if (isCurrentRequest) {
          setLoadError(getErrorMessage(error, 'Failed to load profile data'));
        }
      } finally {
        if (isCurrentRequest) setIsLoading(false);
      }
    };

    void fetchProfile();

    return () => {
      isCurrentRequest = false;
    };
  }, []);

  const fallbackProfile: ProfileData | null = user
    ? {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
      }
    : null;
  const displayData = profileData || fallbackProfile;
  const role = displayData?.role || user?.role || '';
  const isDoctor = role === ROLES.DOCTOR;
  const isPublic = role === ROLES.PUBLIC;

  useEffect(() => {
    if (!profileData || isEditMode) return;
    setEditedName(profileData.name || '');
    setEditedEmail(profileData.email || '');
    setEditedMedicalLicenseNumber(profileData.medicalLicenseNumber || profileData.licenseNumber || '');
    setEditedDepartment(profileData.department || profileData.specialization || '');
  }, [profileData, isEditMode]);

  useEffect(() => {
    if (!isDoctor) {
      setSubscriptionPlan(null);
      setSubscriptionError(null);
      setIsLoadingSubscription(false);
      return;
    }

    let isCurrentRequest = true;
    const fetchSubscription = async () => {
      setIsLoadingSubscription(true);
      setSubscriptionError(null);

      try {
        const plan = await getDoctorSubscriptionPlan();
        if (isCurrentRequest) setSubscriptionPlan(plan);
      } catch (error) {
        if (isCurrentRequest) {
          setSubscriptionError(getErrorMessage(error, 'Failed to load your subscription plan'));
        }
      } finally {
        if (isCurrentRequest) setIsLoadingSubscription(false);
      }
    };

    void fetchSubscription();

    return () => {
      isCurrentRequest = false;
    };
  }, [isDoctor]);

  useEffect(() => {
    if (!isPublic) {
      setPublicSubscription(null);
      setPublicSubscriptionError(null);
      setIsLoadingPublicSubscription(false);
      return;
    }

    let isCurrentRequest = true;
    const fetchPublicSubscription = async () => {
      setIsLoadingPublicSubscription(true);
      setPublicSubscriptionError(null);

      try {
        const subscription = await getMyPublicSubscription();
        if (isCurrentRequest) setPublicSubscription(subscription);
      } catch (error) {
        if (isCurrentRequest) {
          setPublicSubscriptionError(
            getErrorMessage(error, 'Failed to load your subscription details')
          );
        }
      } finally {
        if (isCurrentRequest) setIsLoadingPublicSubscription(false);
      }
    };

    void fetchPublicSubscription();

    return () => {
      isCurrentRequest = false;
    };
  }, [isPublic]);

  const populateDrafts = (data: ProfileData | null) => {
    setEditedName(data?.name || '');
    setEditedEmail(data?.email || '');
    setEditedMedicalLicenseNumber(data?.medicalLicenseNumber || data?.licenseNumber || '');
    setEditedDepartment(data?.department || data?.specialization || '');
  };

  const handleEdit = () => {
    populateDrafts(displayData);
    setProfileErrors({});
    setLoadError(null);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    populateDrafts(displayData);
    setProfileErrors({});
    setLoadError(null);
    setIsEditMode(false);
    setIsEmailModalOpen(false);
    setDevOtp(null);
    setOtp('');
    setOtpError(null);
    setResendCooldown(0);
  };

  const handleCancelEmailChange = () => {
    setIsEmailModalOpen(false);
    setOtp('');
    setOtpError(null);
    setDevOtp(null);
    setResendCooldown(0);
    if (displayData?.email) {
      setEditedEmail(displayData.email);
    }
    setIsEditMode(false);
    toast.info('Email change cancelled. Other profile changes were saved.');
  };

  const handleSave = async () => {
    if (!displayData) return;

    const normalizedName = editedName.trim();
    const normalizedEmail = editedEmail.trim().toLowerCase();
    const normalizedMedicalLicense = editedMedicalLicenseNumber.trim();
    const normalizedDepartment = editedDepartment.trim();
    const currentName = displayData.name || '';
    const currentEmail = displayData.email || '';
    const currentMedicalLicense = displayData.medicalLicenseNumber || displayData.licenseNumber || '';
    const currentDepartment = displayData.department || displayData.specialization || '';
    const nameChanged = normalizedName !== currentName;
    const emailChanged = normalizedEmail !== currentEmail.toLowerCase();
    const medicalLicenseChanged = isDoctor && normalizedMedicalLicense !== currentMedicalLicense;
    const departmentChanged = isDoctor && normalizedDepartment !== (currentDepartment || '');
    const errors: Record<string, string> = {};

    if (nameChanged && !normalizedName) {
      errors.name = 'Name is required';
    }
    if (emailChanged && !EMAIL_PATTERN.test(normalizedEmail)) {
      errors.email = 'Enter a valid email address';
    }
    if (medicalLicenseChanged && !normalizedMedicalLicense) {
      errors.medicalLicenseNumber = 'Medical licence number is required';
    }
    if (departmentChanged && normalizedDepartment.length > 100) {
      errors.department = 'Department must be 100 characters or fewer';
    }

    setProfileErrors(errors);
    if (Object.keys(errors).length > 0) return;

    if (!nameChanged && !emailChanged && !medicalLicenseChanged && !departmentChanged) {
      setIsEditMode(false);
      return;
    }

    setIsSaving(true);
    setLoadError(null);
    let updatedProfile = displayData;

    try {
      if (nameChanged) {
        const result = await updateName(normalizedName);
        updatedProfile = { ...updatedProfile, ...result };
        setProfileData(updatedProfile);
        updateUser({ name: result.name });
        toast.success('Name updated successfully');
      }

      if (medicalLicenseChanged) {
        const result = await updateMedicalLicenseNumber(normalizedMedicalLicense);
        updatedProfile = { ...updatedProfile, ...result };
        setProfileData(updatedProfile);
        toast.success('Medical licence number updated successfully');
      }

      if (departmentChanged) {
        const result = await updateDepartment(normalizedDepartment);
        updatedProfile = { ...updatedProfile, ...result };
        setProfileData(updatedProfile);
        toast.success('Department updated successfully');
      }

      if (emailChanged) {
        const result = await requestEmailChange(normalizedEmail);
        setPendingEmail(normalizedEmail);
        setOtp('');
        setOtpError(null);
        if (result.otp) {
          setDevOtp(result.otp);
        }
        setResendCooldown(30);
        setIsEmailModalOpen(true);
        toast.info(result.message || `Verification code sent to ${normalizedEmail}`);
        return;
      }

      setIsEditMode(false);
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to save profile changes');
      setLoadError(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleVerifyOtp = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    const cleanOtp = otp.trim();
    if (!cleanOtp || cleanOtp.length !== 6) {
      setOtpError('Please enter the 6-digit verification code');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError(null);

    try {
      const result = await verifyEmailChange(cleanOtp);
      const baseProfile = profileData || fallbackProfile;
      const updatedProfile = baseProfile ? { ...baseProfile, ...result } : result;
      setProfileData(updatedProfile);
      setEditedEmail(result.email);
      updateUser({ email: result.email });
      setIsEmailModalOpen(false);
      setIsEditMode(false);
      setDevOtp(null);
      setOtp('');
      toast.success('Email updated successfully! You can now use this email to log in.');
    } catch (error) {
      const message = getErrorMessage(error, 'The verification code is invalid or expired');
      setOtpError(message);
      toast.error(message);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingEmail || resendCooldown > 0 || isResendingOtp) return;

    setIsResendingOtp(true);
    setOtpError(null);

    try {
      const result = await requestEmailChange(pendingEmail);
      if (result.otp) {
        setDevOtp(result.otp);
      }
      setResendCooldown(30);
      toast.info(result.message || `A new verification code was sent to ${pendingEmail}`);
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to resend verification code');
      setOtpError(message);
      toast.error(message);
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const errors: Record<string, string> = {};

    if (!currentPassword) errors.currentPassword = 'Current password is required';
    const passwordValidationError = validateNewPassword(newPassword);
    if (passwordValidationError) errors.newPassword = passwordValidationError;
    if (newPassword !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (currentPassword && currentPassword === newPassword) {
      errors.newPassword = 'New password must be different from the current password';
    }

    setPasswordErrors(errors);
    setPasswordSubmitError(null);
    if (Object.keys(errors).length > 0) return;

    setIsChangingPassword(true);

    try {
      const result = await changePassword(currentPassword, newPassword, confirmPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success(result.message || 'Password changed successfully');
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to change password');
      setPasswordSubmitError(message);
      toast.error(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (isLoading && !displayData) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-neutral-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!displayData) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="w-full max-w-md rounded-lg border border-neutral-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 text-danger-600">
            <AlertCircle className="h-5 w-5" />
            <p className="font-medium">Unable to load profile</p>
          </div>
          <p className="mt-2 text-sm text-neutral-600">{loadError || 'Profile data is unavailable.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Profile</h1>
          <p className="mt-1 text-sm text-neutral-500">Manage your account and professional information.</p>
        </div>
        {!isEditMode && (
          <button
            type="button"
            onClick={handleEdit}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            <Edit2 className="h-4 w-4" />
            Edit profile
          </button>
        )}
      </div>

      {loadError && (
        <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      <section className="rounded-lg border border-neutral-200 bg-white shadow-sm">
        <div className="border-b border-neutral-200 p-5 sm:p-6">
          <div className="flex items-center gap-5">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-primary-100">
              <User className="h-10 w-10 text-primary-600" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-xl font-semibold text-neutral-900">{displayData.name}</h2>
              <div className="mt-2 flex items-center gap-2 text-sm font-medium text-neutral-600">
                <Shield className="h-4 w-4 text-neutral-400" />
                <span>{getRoleLabel(role)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6 p-5 sm:p-6">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {isEditMode && (
              <Field label="Name" htmlFor="profile-name" error={profileErrors.name}>
                <input
                  id="profile-name"
                  type="text"
                  value={editedName}
                  onChange={(event) => setEditedName(event.target.value)}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                />
              </Field>
            )}

            {isEditMode ? (
              <Field label="Email Address" htmlFor="profile-email" error={profileErrors.email}>
                <input
                  id="profile-email"
                  type="email"
                  value={editedEmail}
                  onChange={(event) => setEditedEmail(event.target.value)}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                />
              </Field>
            ) : (
              <div>
                <p className="mb-2 text-sm font-medium text-neutral-700">Email Address</p>
                <div className="flex items-center gap-2 text-neutral-900">
                  <Mail className="h-4 w-4 text-neutral-400" />
                  <span>{displayData.email}</span>
                </div>
              </div>
            )}

            {isDoctor && (
              isEditMode ? (
                <Field
                  label="Medical Licence Number"
                  htmlFor="profile-medical-license"
                  error={profileErrors.medicalLicenseNumber}
                >
                  <div className="relative">
                    <FileText className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                    <input
                      id="profile-medical-license"
                      type="text"
                      value={editedMedicalLicenseNumber}
                      onChange={(event) => setEditedMedicalLicenseNumber(event.target.value)}
                      className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter your medical licence number"
                    />
                  </div>
                </Field>
              ) : (
                <div>
                  <p className="mb-2 text-sm font-medium text-neutral-700">Medical Licence Number</p>
                  <div className="flex items-center gap-2 text-neutral-900">
                    <FileText className="h-4 w-4 text-neutral-400" />
                    <span>{displayData.medicalLicenseNumber || displayData.licenseNumber || 'Not specified'}</span>
                  </div>
                </div>
              )
            )}

            {isDoctor && (
              isEditMode ? (
                <Field label="Department" htmlFor="profile-department" error={profileErrors.department}>
                  <div className="relative">
                    <Briefcase className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                    <input
                      id="profile-department"
                      type="text"
                      value={editedDepartment}
                      onChange={(event) => setEditedDepartment(event.target.value)}
                      maxLength={100}
                      className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter your department"
                    />
                  </div>
                </Field>
              ) : (
                <div>
                  <p className="mb-2 text-sm font-medium text-neutral-700">Department</p>
                  <div className="flex items-center gap-2 text-neutral-900">
                    <Briefcase className="h-4 w-4 text-neutral-400" />
                    <span>{displayData.department || displayData.specialization || 'Not specified'}</span>
                  </div>
                </div>
              )
            )}

            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700">Role</p>
              <div className="flex items-center gap-2 text-neutral-900">
                <Shield className="h-4 w-4 text-neutral-400" />
                <span>{getRoleLabel(role)}</span>
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700">Account Status</p>
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                  displayData.active
                    ? 'bg-success-50 text-success-600'
                    : 'bg-danger-50 text-danger-600'
                }`}
              >
                {displayData.active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {isEditMode && (
            <div className="flex flex-col-reverse gap-3 border-t border-neutral-200 pt-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-medium text-neutral-800 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isSaving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
      </section>

      {isDoctor && (
        <section className="rounded-lg border border-neutral-200 bg-white shadow-sm">
          <div className="border-b border-neutral-200 p-5 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-neutral-900">Doctor Professional Details</h2>
              <span className="flex items-center gap-1 text-xs font-medium text-neutral-500">
                <Lock className="h-3 w-3 text-neutral-400" />
                Administrative fields are managed by Super Admin
              </span>
            </div>
          </div>
          <dl className="grid grid-cols-1 gap-5 p-5 sm:grid-cols-2 sm:p-6">
            <Detail label="Specialization" icon={Briefcase} value={displayData.specialization} />
            <Detail label="Hospital" icon={Building2} value={displayData.hospitalName} />
            <Detail label="Phone Number" icon={Phone} value={displayData.phoneNumber} />
            <Detail label="Medical Registration Number" icon={FileText} value={displayData.medicalRegistrationNumber} />
            <Detail label="Qualification" icon={Award} value={displayData.qualification} />
            <Detail
              label="Years of Experience"
              icon={Award}
              value={
                displayData.yearsOfExperience === null || displayData.yearsOfExperience === undefined
                  ? null
                  : `${displayData.yearsOfExperience} Years`
              }
            />
            <Detail label="Onboarding Status" icon={Shield} value={displayData.onboardingStatus} />
            {displayData.updatedAt && (
              <Detail
                label="Last Updated"
                icon={History}
                value={formatAuditDate(displayData.updatedAt)}
              />
            )}
          </dl>
        </section>
      )}

      {role === ROLES.SUPERADMIN && (
        <section className="rounded-lg border border-neutral-200 bg-white shadow-sm">
          <div className="border-b border-neutral-200 p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-neutral-900">System Administration</h2>
            <p className="text-sm text-neutral-500">Your platform-wide permissions.</p>
          </div>
          <div className="p-5 sm:p-6">
            <Detail
              label="Access Level"
              icon={Shield}
              value={displayData.accessLevel || 'Full System Access'}
            />
          </div>
        </section>
      )}

      {isDoctor && (
        <section className="rounded-lg border border-neutral-200 bg-white shadow-sm">
          <div className="border-b border-neutral-200 p-5 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                <CreditCard className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Subscription Plan</h2>
                <p className="text-sm text-neutral-500">Your currently assigned plan.</p>
              </div>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            {isLoadingSubscription ? (
              <div className="flex items-center gap-3 text-sm text-neutral-500">
                <Loader className="h-5 w-5 animate-spin text-primary-600" />
                Loading subscription plan...
              </div>
            ) : subscriptionError ? (
              <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{subscriptionError}</span>
              </div>
            ) : !subscriptionPlan ? (
              <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center">
                <Award className="mx-auto h-8 w-8 text-neutral-400" />
                <p className="mt-3 text-sm font-medium text-neutral-700">No subscription plan is currently assigned.</p>
                <p className="mt-1 text-sm text-neutral-500">Contact the administrator for plan assistance.</p>
              </div>
            ) : (
              <div>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Plan</p>
                    <h3 className="mt-1 text-xl font-semibold text-neutral-900">{subscriptionPlan.planName}</h3>
                  </div>
                  <div className="rounded-lg bg-primary-50 px-4 py-3 text-primary-800 sm:text-right">
                    <p className="text-xs font-medium">Price</p>
                    <p className="text-lg font-bold">{formatPlanPrice(subscriptionPlan)}</p>
                    <p className="text-xs">{subscriptionPlan.currency}</p>
                  </div>
                </div>
                <dl className="mt-5 grid grid-cols-1 gap-4 border-y border-neutral-200 py-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Currency</dt>
                    <dd className="mt-1 text-sm text-neutral-900">{subscriptionPlan.currency}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Duration</dt>
                    <dd className="mt-1 text-sm text-neutral-900">
                      {subscriptionPlan.durationValue} {subscriptionPlan.durationType}
                      {subscriptionPlan.durationValue === 1 ? '' : 's'}
                    </dd>
                  </div>
                </dl>
                <div className="mt-5">
                  <p className="text-sm font-semibold text-neutral-800">Included features</p>
                  {subscriptionPlan.includedFeatures?.length ? (
                    <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {subscriptionPlan.includedFeatures.map((feature, index) => (
                        <li key={`${feature}-${index}`} className="flex items-start gap-2 text-sm text-neutral-700">
                          <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-success-600" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-neutral-500">No features are listed for this plan.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {isPublic && (
        <section className="rounded-lg border border-neutral-200 bg-white shadow-sm">
          <div className="border-b border-neutral-200 p-5 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                <CreditCard className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Subscription Plan</h2>
                <p className="text-sm text-neutral-500">Your current subscription and billing details.</p>
              </div>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            {isLoadingPublicSubscription ? (
              <div className="flex items-center gap-3 text-sm text-neutral-500">
                <Loader className="h-5 w-5 animate-spin text-primary-600" />
                Loading subscription details...
              </div>
            ) : publicSubscriptionError ? (
              <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{publicSubscriptionError}</span>
              </div>
            ) : !publicSubscription ? (
              <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center">
                <Award className="mx-auto h-8 w-8 text-neutral-400" />
                <p className="mt-3 text-sm font-medium text-neutral-700">
                  No subscription plan is currently associated with your account.
                </p>
                <p className="mt-1 text-sm text-neutral-500">Contact the administrator for plan assistance.</p>
              </div>
            ) : (
              <div>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Plan</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <h3 className="text-xl font-semibold text-neutral-900">
                        {publicSubscription.planName || 'Subscription'}
                      </h3>
                      {publicSubscription.planTag && (
                        <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-primary-700">
                          {publicSubscription.planTag}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg bg-primary-50 px-4 py-3 text-primary-800 sm:text-right">
                    <p className="text-xs font-medium">Amount</p>
                    <p className="text-lg font-bold">
                      {formatSubscriptionAmount(publicSubscription.amount, publicSubscription.currency || 'USD')}
                    </p>
                    <p className="text-xs">
                      {publicSubscription.billingCycle
                        ? `${publicSubscription.billingCycle} billing`
                        : publicSubscription.currency || 'USD'}
                    </p>
                  </div>
                </div>
                <dl className="mt-5 grid grid-cols-1 gap-4 border-y border-neutral-200 py-4 sm:grid-cols-2">
                  <div>
                    <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      <Shield className="h-3.5 w-3.5 text-neutral-400" />
                      Status
                    </dt>
                    <dd className="mt-1">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${subscriptionStatusClasses(
                          publicSubscription.status
                        )}`}
                      >
                        {publicSubscription.status || 'Unknown'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      <History className="h-3.5 w-3.5 text-neutral-400" />
                      Billing Cycle
                    </dt>
                    <dd className="mt-1 text-sm text-neutral-900">
                      {publicSubscription.billingCycle || 'Not specified'}
                    </dd>
                  </div>
                  <div>
                    <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      <History className="h-3.5 w-3.5 text-neutral-400" />
                      Start Date
                    </dt>
                    <dd className="mt-1 text-sm text-neutral-900">
                      {formatSubscriptionDate(publicSubscription.startDate)}
                    </dd>
                  </div>
                  <div>
                    <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      <History className="h-3.5 w-3.5 text-neutral-400" />
                      End Date
                    </dt>
                    <dd className="mt-1 text-sm text-neutral-900">
                      {formatSubscriptionDate(publicSubscription.endDate)}
                    </dd>
                  </div>
                  {publicSubscription.plan && (
                    <div>
                      <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        <Award className="h-3.5 w-3.5 text-neutral-400" />
                        Duration
                      </dt>
                      <dd className="mt-1 text-sm text-neutral-900">
                        {publicSubscription.plan.durationValue} {publicSubscription.plan.durationType}
                        {publicSubscription.plan.durationValue === 1 ? '' : 's'}
                      </dd>
                    </div>
                  )}
                  <div>
                    <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                      <CheckCircle className="h-3.5 w-3.5 text-neutral-400" />
                      Auto Renew
                    </dt>
                    <dd className="mt-1 text-sm text-neutral-900">
                      {publicSubscription.autoRenew ? 'Enabled' : 'Disabled'}
                    </dd>
                  </div>
                </dl>
                {publicSubscription.plan?.includedFeatures?.length ? (
                  <div className="mt-5">
                    <p className="text-sm font-semibold text-neutral-800">Included features</p>
                    <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {publicSubscription.plan.includedFeatures.map((feature, index) => (
                        <li key={`${feature}-${index}`} className="flex items-start gap-2 text-sm text-neutral-700">
                          <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-success-600" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="rounded-lg border border-neutral-200 bg-white shadow-sm">
        <div className="border-b border-neutral-200 p-5 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
              <KeyRound className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Change Password</h2>
              <p className="text-sm text-neutral-500">Use a strong password that you do not use elsewhere.</p>
            </div>
          </div>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-5 p-5 sm:p-6" noValidate>
          {passwordSubmitError && (
            <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{passwordSubmitError}</span>
            </div>
          )}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <Field label="Current Password" htmlFor="current-password" error={passwordErrors.currentPassword}>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              />
            </Field>
            <Field label="New Password" htmlFor="new-password" error={passwordErrors.newPassword}>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              />
            </Field>
            <Field label="Confirm New Password" htmlFor="confirm-password" error={passwordErrors.confirmPassword}>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-800 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              />
            </Field>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isChangingPassword}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isChangingPassword ? <Loader className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              {isChangingPassword ? 'Updating...' : 'Change password'}
            </button>
          </div>
        </form>
      </section>

      {/* Email Verification Modal */}
      {isEmailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-6 shadow-xl transition-all">
            <div className="flex items-center justify-between border-b border-neutral-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">Verify New Email</h3>
                  <p className="text-xs text-neutral-500">Confirm your email address update</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCancelEmailChange}
                disabled={isVerifyingOtp}
                className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition"
                aria-label="Close dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleVerifyOtp} className="mt-5 space-y-4">
              <p className="text-sm text-neutral-600 leading-relaxed">
                A 6-digit verification code was sent to{' '}
                <span className="font-semibold text-neutral-900">{pendingEmail}</span>.
                Enter the code below to complete changing your email.
              </p>

              <div>
                <label htmlFor="email-otp-input" className="block text-sm font-medium text-neutral-700 mb-1">
                  6-Digit Verification Code
                </label>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    id="email-otp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtp(val);
                      if (otpError) setOtpError(null);
                    }}
                    placeholder="••••••"
                    className="w-full rounded-lg border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-center font-mono text-lg tracking-[0.3em] text-neutral-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
                    autoFocus
                  />
                </div>
                {otpError && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-danger-600">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    <span>{otpError}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || isResendingOtp || isVerifyingOtp}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:text-neutral-400 disabled:cursor-not-allowed transition"
                >
                  {isResendingOtp
                    ? 'Resending...'
                    : resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : 'Resend code'}
                </button>
                <span className="text-xs text-neutral-400">Code valid for 10 minutes</span>
              </div>

              <div className="flex items-center justify-end gap-2.5 border-t border-neutral-100 pt-4">
                <button
                  type="button"
                  onClick={handleCancelEmailChange}
                  disabled={isVerifyingOtp}
                  className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isVerifyingOtp || otp.length !== 6}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition disabled:opacity-50"
                >
                  {isVerifyingOtp ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Verify & Update</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dev/QA OTP badge — visible when email change modal is open & OTP is available */}
      {isEmailModalOpen && devOtp && (
        <div className="fixed bottom-4 right-4 z-[60] rounded-lg bg-neutral-900/90 px-4 py-2 text-sm font-mono text-white shadow-lg backdrop-blur-sm">
          <span className="opacity-60">Dev OTP:</span>{' '}
          <span className="font-bold tracking-widest">{devOtp}</span>
        </div>
      )}
    </div>
  );
};

export default Profile;
